import type { PanoramaBlock } from '$models/Block/PanoramaBlock';
import type { AccountAddressDetailPage as AccountAddressDetailPageType } from '$models/Page/AccountAddressDetailPage';
import type { AccountAddressesPage as AccountAddressesPageType } from '$models/Page/AccountAddressesPage';
import type { AccountCustomerInfoPage as AccountCustomerInfoPageType } from '$models/Page/AccountCustomerInfoPage';
import type { AccountOrderDetailPage as AccountOrderDetailPageType } from '$models/Page/AccountOrderDetailPage';
import type { AccountOrdersPage as AccountOrdersPageType } from '$models/Page/AccountOrdersPage';
import type { AccountPage as AccountPageType } from '$models/Page/AccountPage';
import type { AccountSubscriptionDetailPage as AccountSubscriptionDetailPageType } from '$models/Page/AccountSubscriptionDetailPage';
import type { AccountSubscriptionsPage as AccountSubscriptionsPageType } from '$models/Page/AccountSubscriptionsPage';
import type { AuthorPage as AuthorPageType } from '$models/Page/AuthorPage';
import type { BlogArticlePage as BlogArticlePageType } from '$models/Page/BlogArticlePage';
import type { BlogOverviewPage as BlogOverviewPageType } from '$models/Page/BlogOverviewPage';
import type { CartPage as CartPageType } from '$models/Page/CartPage';
import type { CheckoutPage as CheckoutPageType } from '$models/Page/CheckoutPage';
import type { ContentPage as ContentPageType } from '$models/Page/ContentPage';
import type { LandingPage as LandingPageType } from '$models/Page/LandingPage';
import type { OrderStatusPage as OrderStatusPageType } from '$models/Page/OrderStatusPage';
import type { Page, PageType } from '$models/Page/Page';
import type { ShopDetailPage as ShopDetailPageType } from '$models/Page/ShopDetailPage';
import type { ShopPage as ShopPageType } from '$models/Page/ShopPage';
import type { Component } from 'svelte';
import AuthorPage from '$components/Page/AuthorPage.svelte';
import BlogArticlePage from '$components/Page/BlogArticlePage.svelte';
import BlogOverviewPage from '$components/Page/BlogOverviewPage.svelte';
import ContentPage from '$components/Page/ContentPage.svelte';
import LandingPage from '$components/Page/LandingPage.svelte';
import { getPanoramaPreloadLinks } from '$lib/panorama';

export interface PageComponentData {
	component: Component<any>;
	layout: Component<any> | null;
	page: PageType;
}

type PanoramaPreloadLink = {
	href: string;
	media: string;
};

export function getFirstPanoramaBlock(page: Page): PanoramaBlock | null {
	if (!('blocks' in page) || !Array.isArray(page.blocks) || page.blocks.length === 0) {
		return null;
	}

	const [firstBlock] = page.blocks;

	if (!firstBlock || firstBlock.type !== 'panorama') {
		return null;
	}

	return firstBlock as PanoramaBlock;
}

export function getFirstPanoramaPreloadLinks(page: Page): PanoramaPreloadLink[] {
	const panoramaBlock = getFirstPanoramaBlock(page);

	if (!panoramaBlock) {
		return [];
	}

	return getPanoramaPreloadLinks(panoramaBlock);
}

export function getPageComponentData(
	page: Page
): PageComponentData | Promise<PageComponentData | null> | null {
	switch (page.type) {
		case 'content':
			return {
				layout: null,
				component: ContentPage,
				page: page as ContentPageType
			};
		case 'landing':
			return {
				layout: null,
				component: LandingPage,
				page: page as LandingPageType
			};
		case 'blogArticle':
			return {
				layout: null,
				component: BlogArticlePage,
				page: page as BlogArticlePageType
			};
		case 'blogOverview':
			return {
				layout: null,
				component: BlogOverviewPage,
				page: page as BlogOverviewPageType
			};
		case 'cart':
			return import('$components/Page/CartPage.svelte').then(({ default: Comp }) => ({
				layout: null,
				component: Comp,
				page: page as CartPageType
			}));
		case 'checkout':
			return import('$components/Page/CheckoutPage.svelte').then(({ default: Comp }) => ({
				layout: null,
				component: Comp,
				page: page as CheckoutPageType
			}));
		case 'account':
			return Promise.all([
				import('$components/Page/AccountPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountPageType
			}));
		case 'account-customer-info':
			return Promise.all([
				import('$components/Page/AccountCustomerInfoPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountCustomerInfoPageType
			}));
		case 'account-orders':
			return Promise.all([
				import('$components/Page/AccountOrdersPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountOrdersPageType
			}));
		case 'account-order-detail':
			return Promise.all([
				import('$components/Page/AccountOrderDetailPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountOrderDetailPageType
			}));
		case 'account-subscriptions':
			return Promise.all([
				import('$components/Page/AccountSubscriptionsPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountSubscriptionsPageType
			}));
		case 'account-subscription-detail':
			return Promise.all([
				import('$components/Page/AccountSubscriptionDetailPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountSubscriptionDetailPageType
			}));
		case 'account-addresses':
			return Promise.all([
				import('$components/Page/AccountAddressesPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountAddressesPageType
			}));
		case 'account-address-detail':
			return Promise.all([
				import('$components/Page/AccountAddressDetailPage.svelte'),
				import('$components/Account/AccountLayout.svelte')
			]).then(([{ default: Comp }, { default: Layout }]) => ({
				layout: Layout,
				component: Comp,
				page: page as AccountAddressDetailPageType
			}));
		case 'order-status':
			return import('$components/Page/OrderStatusPage.svelte').then(({ default: Comp }) => ({
				layout: null,
				component: Comp,
				page: page as OrderStatusPageType
			}));
		case 'shop':
			return import('$components/Page/ShopPage.svelte').then(({ default: Comp }) => ({
				layout: null,
				component: Comp,
				page: page as ShopPageType
			}));
		case 'shop-item-detail':
			return import('$components/Page/ShopDetailPage.svelte').then(({ default: Comp }) => ({
				layout: null,
				component: Comp,
				page: page as ShopDetailPageType
			}));
		case 'author':
			return {
				layout: null,
				component: AuthorPage,
				page: page as AuthorPageType
			};
		default:
			return null;
	}
}
